import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Users2, Gift, CheckCircle2, Search, Building2, Loader2, Shield, Plus, Trash2, Edit2, AlertTriangle, LogIn, KeyRound, Copy, Eye, EyeOff, Mail } from "lucide-react";

const PLAN_META = {
  free:       { label: "Free (Admin)", tone: "bg-violet-100 text-violet-800" },
  starter:    { label: "Starter",      tone: "bg-blue-100 text-blue-800" },
  pro:        { label: "Pro",          tone: "bg-emerald-100 text-emerald-800" },
  enterprise: { label: "Enterprise",   tone: "bg-amber-100 text-amber-800" },
};

export default function AdminLandlords() {
  const [items, setItems] = useState([]);
  const [logos, setLogos] = useState({});  // { user_id: data_uri }
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [editingProfile, setEditingProfile] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [resetting, setResetting] = useState(null);  // the user whose pwd to reset
  const [sendingLinkFor, setSendingLinkFor] = useState(null);  // in-flight send-reset-link user id

  const sendResetLink = async (l) => {
    if (sendingLinkFor) return;
    const proceed = window.confirm(
      `Send a password-reset email to ${l.email}?\n\nThe link expires in 60 minutes and the admin will never see the password.`
    );
    if (!proceed) return;
    setSendingLinkFor(l.id);
    try {
      const { data } = await api.post(`/admin/users/${l.id}/send-reset-link`);
      if (data.dev_reset_url) {
        // MOCK-email mode: surface the link so the admin can copy-paste it
        toast.success("Email provider not configured — link logged to console");
        // eslint-disable-next-line no-console
        console.info("[admin-send-reset-link] DEV RESET URL:", data.dev_reset_url);
      } else {
        toast.success(`Reset link sent to ${data.user_email}. Expires in ${data.expires_in_minutes} min.`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send reset link");
    } finally {
      setSendingLinkFor(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/landlords");
      setItems(data);
      // Lazy-load logos in parallel batches of up to 50 ids each. We
      // fetch only ids whose `has_logo=true` so the request stays compact
      // when most users haven't uploaded one.
      const idsWithLogos = data.filter((u) => u.has_logo).map((u) => u.id);
      if (idsWithLogos.length) {
        const chunks = [];
        for (let i = 0; i < idsWithLogos.length; i += 50) {
          chunks.push(idsWithLogos.slice(i, i + 50));
        }
        const results = await Promise.all(
          chunks.map((batch) =>
            api.get(`/admin/landlords/logos?ids=${batch.join(",")}`).then((r) => r.data)
          ),
        );
        const merged = Object.assign({}, ...results);
        setLogos(merged);
      } else {
        setLogos({});
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load landlords");
    } finally {
      setLoading(false);
    }
  };

  /**
   * "Login As User" — admin support tool. Backs up the admin's current
   * token under `rv_admin_token_backup` so the ImpersonationBanner can
   * later restore it, then swaps in the impersonation token and hard-
   * navigates to /dashboard. Hard navigation forces a full app reload so
   * the AuthContext re-initializes from the new token cleanly.
   */
  const impersonate = async (target) => {
    if (!window.confirm(
      `Sign in as ${target.full_name || target.email}?\n\n` +
      `This is logged in the admin audit trail. You'll be redirected to their dashboard. ` +
      `Click "Exit impersonation" in the top banner to return to your admin account.`
    )) return;
    try {
      const { data } = await api.post(`/admin/landlords/${target.id}/impersonate`);
      const adminToken = localStorage.getItem("rv_token");
      if (adminToken) localStorage.setItem("rv_admin_token_backup", adminToken);
      localStorage.setItem("rv_token", data.token);
      // Hard reload (not navigate) so AuthContext refetches /auth/me with
      // the new token instead of holding the admin's cached user object.
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not impersonate user");
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter((l) =>
      (l.email || "").toLowerCase().includes(needle) ||
      (l.full_name || "").toLowerCase().includes(needle) ||
      (l.company || "").toLowerCase().includes(needle)
    );
  }, [items, q]);

  const freeCount = useMemo(
    () => items.filter((l) => l.subscription_plan === "free").length,
    [items]
  );

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-start justify-between mb-6 gap-3 flex-wrap bg-green-50 border border-green-200 rounded-sm p-5" data-testid="platform-users-header">
          <div>
            <div className="overline text-slate-500">Platform</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit" }} data-testid="admin-landlords-heading">
              <Users2 className="w-7 h-7 text-slate-700" /> Platform Users
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage subscriptions and grant comped accounts.{" "}
              <span className="text-slate-900 font-medium">{items.length}</span> total ·{" "}
              <span className="text-violet-700 font-medium">{freeCount}</span> on free grants.
            </p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            className="input-clean flex-1 border-0 focus:ring-0"
            placeholder="Search by name, email, or company…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-landlords-search"
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left whitespace-nowrap">
              <tr>
                <Th>Platform user</Th>
                <Th>Plan</Th>
                <Th>Status</Th>
                <Th>Properties</Th>
                <Th>Tenants</Th>
                <Th>Tour</Th>
                <Th>Last activity</Th>
                <Th>Joined</Th>
                <Th className="text-right">Action</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="text-center text-sm text-slate-400 py-12">Loading landlords…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center text-sm text-slate-400 py-12">No landlords match "{q}".</td></tr>
              )}
              {!loading && filtered.map((l) => {
                const plan = l.subscription_plan || "none";
                const meta = PLAN_META[plan];
                const isActive = l.subscription_status === "active";
                return (
                  <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-landlord-row-${l.id}`}>
                    <Td>
                      <div className="flex items-center gap-2.5">
                        {logos[l.id] ? (
                          <img
                            src={logos[l.id]}
                            alt={l.company || l.full_name || "Logo"}
                            className="w-8 h-8 object-contain rounded-sm border border-slate-200 bg-white p-0.5 shrink-0"
                            data-testid={`admin-landlord-logo-${l.id}`}
                          />
                        ) : (
                          <div
                            className="w-8 h-8 rounded-sm bg-slate-100 text-slate-400 text-[10px] font-mono flex items-center justify-center shrink-0"
                            title={l.has_logo ? "Loading logo…" : "No brand logo uploaded"}
                            data-testid={`admin-landlord-logo-placeholder-${l.id}`}
                          >
                            {(l.company || l.full_name || l.email || "?").trim().charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <Link
                            to={`/dashboard/admin/landlords/${l.id}/activity`}
                            className="font-bold text-slate-900 truncate hover:text-emerald-700 hover:underline block"
                            style={{ fontFamily: "Outfit" }}
                            data-testid={`admin-landlord-name-link-${l.id}`}
                            title="View activity timeline"
                          >
                            {l.full_name || l.email}
                          </Link>
                          <div className="text-xs text-slate-500 font-mono truncate">{l.email}{l.company ? ` · ${l.company}` : ""}</div>
                        </div>
                      </div>
                    </Td>
                    <Td>
                      {meta ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest ${meta.tone}`}>
                          {plan === "free" && <Gift className="w-3 h-3" />}
                          {meta.label}
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Trial / none</span>
                      )}
                      {l.admin_granted_plan && (
                        <div className="text-[10px] font-mono uppercase tracking-widest text-violet-700 mt-0.5">Admin-granted</div>
                      )}
                    </Td>
                    <Td>
                      {isActive ? (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">● Active</span>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">○ Inactive</span>
                      )}
                    </Td>
                    <Td className="font-mono text-xs">{l.property_count ?? "—"}</Td>
                    <Td className="font-mono text-xs">{l.tenant_count ?? 0}</Td>
                    <Td data-testid={`tour-status-${l.id}`}>
                      {l.tour_completed_at ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700" title={new Date(l.tour_completed_at).toLocaleString()}>
                          <CheckCircle2 className="w-3 h-3" /> Done
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">—</span>
                      )}
                    </Td>
                    <Td data-testid={`last-activity-${l.id}`}>
                      {l.last_login_at ? (
                        <div className="flex flex-col" title={new Date(l.last_login_at).toLocaleString() + (l.last_login_ip ? ` · ${l.last_login_ip}` : "")}>
                          <span className="text-xs font-mono text-slate-700">
                            {formatRelative(l.last_login_at)}
                          </span>
                          {l.last_login_ip && (
                            <span className="text-[10px] font-mono text-slate-400">{l.last_login_ip}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400" title="Never signed in">Never</span>
                      )}
                    </Td>
                    <Td className="font-mono text-xs text-slate-500">
                      {l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}
                    </Td>
                    <Td className="text-right whitespace-nowrap">
                      <div className="inline-flex items-center justify-end gap-0.5">
                        <button
                          onClick={() => setEditingProfile(l)}
                          className="p-1.5 rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                          data-testid={`edit-landlord-${l.id}`}
                          title="Edit profile"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditing(l)}
                          className="p-1.5 rounded text-slate-500 hover:text-indigo-700 hover:bg-indigo-50"
                          data-testid={`change-plan-${l.id}`}
                          title="Change subscription plan"
                        >
                          <Gift className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => impersonate(l)}
                          className="p-1.5 rounded text-slate-500 hover:text-emerald-700 hover:bg-emerald-50"
                          data-testid={`login-as-${l.id}`}
                          title="Sign in as this user (audit-logged)"
                        >
                          <LogIn className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setResetting(l)}
                          className="p-1.5 rounded text-slate-500 hover:text-amber-700 hover:bg-amber-50"
                          data-testid={`reset-pw-${l.id}`}
                          title="Reset password and reveal the new one"
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => sendResetLink(l)}
                          disabled={sendingLinkFor === l.id}
                          className="p-1.5 rounded text-slate-500 hover:text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                          data-testid={`send-reset-link-${l.id}`}
                          title="Email a 60-min single-use reset link to the user (admin never sees the password)"
                        >
                          {sendingLinkFor === l.id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <Mail className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => setDeleting(l)}
                          className="p-1.5 rounded text-slate-400 hover:text-rose-700 hover:bg-rose-50"
                          data-testid={`delete-landlord-${l.id}`}
                          title="Delete landlord"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <ChangePlanDialog
          landlord={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />

        <EditLandlordDialog
          landlord={editingProfile}
          onClose={() => setEditingProfile(null)}
          onSaved={() => { setEditingProfile(null); load(); }}
        />

        <DeleteLandlordDialog
          landlord={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); load(); }}
        />

        <ResetPasswordDialog
          user={resetting}
          onClose={() => setResetting(null)}
        />

        <AdminsSection />
      </div>
    </DashboardShell>
  );
}

function AdminsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/admins");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load admins");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    if ((form.password || "").length < 8) {
      toast.error("Admin password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/admins", {
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        password: form.password,
      });
      toast.success(`${form.full_name} added as admin.`);
      setOpen(false);
      setForm({ email: "", full_name: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.email} as an admin?`)) return;
    try {
      await api.delete(`/admin/admins/${a.id}`);
      toast.success(`${a.email} removed`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Remove failed");
    }
  };

  return (
    <section className="mt-8" data-testid="admins-section">
      <div className="flex items-start justify-between mb-4 bg-amber-50 border border-amber-200 rounded-sm p-5" data-testid="platform-admins-header">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Shield className="w-5 h-5 text-slate-700" /> Platform Admins
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">People with full access to every landlord and platform setting.</p>
        </div>
        <button onClick={() => setOpen(true)} className="btn-primary-dark text-sm flex items-center gap-2" data-testid="admin-invite-button">
          <Plus className="w-4 h-4" /> Add admin
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-left">
            <tr>
              <th className="px-5 py-3 overline text-slate-600">Name</th>
              <th className="px-5 py-3 overline text-slate-600">Email</th>
              <th className="px-5 py-3 overline text-slate-600">Joined</th>
              <th className="px-5 py-3 overline text-slate-600 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="text-center text-sm text-slate-400 py-8">Loading…</td></tr>}
            {!loading && items.map((a) => (
              <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-row-${a.id}`}>
                <td className="px-5 py-3 font-bold text-slate-900">{a.full_name || "—"}</td>
                <td className="px-5 py-3 text-xs font-mono text-slate-700">{a.email}</td>
                <td className="px-5 py-3 text-xs font-mono text-slate-500">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => remove(a)} className="text-slate-500 hover:text-red-600 p-1" data-testid={`admin-remove-${a.id}`} title="Remove"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!v) setOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="admin-invite-dialog">
          <DialogHeader><DialogTitle style={{fontFamily:"Outfit"}}>Add a platform admin</DialogTitle></DialogHeader>
          <form onSubmit={invite} className="space-y-3">
            <p className="text-xs text-slate-500">Admins have full access to every landlord, the revenue dashboard, integrations, and plan grants. Invite sparingly.</p>
            <Field label="Full name"><input required className="input-clean w-full" data-testid="admin-invite-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></Field>
            <Field label="Email"><input required type="email" className="input-clean w-full" data-testid="admin-invite-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Temporary password (min 8)"><input required minLength={8} className="input-clean w-full" data-testid="admin-invite-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="btn-outline-dark">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary-dark flex items-center gap-2" data-testid="admin-invite-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />} Add admin
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function ChangePlanDialog({ landlord, onClose, onSaved }) {
  const open = !!landlord;
  const [plan, setPlan] = useState("free");
  const [months, setMonths] = useState(12);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (landlord) {
      setPlan("free");
      setMonths(12);
      setReason("");
    }
  }, [landlord]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/landlords/${landlord.id}/plan`, {
        plan, months: Number(months) || 12, reason: reason.trim() || null,
      });
      toast.success(`${landlord.email} → ${data.landlord.subscription_plan} (${months} months)`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update plan.");
    } finally {
      setBusy(false);
    }
  };

  if (!landlord) return <Dialog open={false} onOpenChange={() => {}}><DialogContent /></Dialog>;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="change-plan-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-violet-600" /> Change plan
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="rounded-sm bg-slate-50 border border-slate-200 p-3 text-sm">
            <div className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{landlord.full_name || landlord.email}</div>
            <div className="text-xs font-mono text-slate-500 mt-0.5">{landlord.email}</div>
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
              <Building2 className="w-3 h-3" /> {landlord.property_count ?? 0} props · {landlord.tenant_count ?? 0} tenants · currently{" "}
              <b>{landlord.subscription_plan || "trial"}</b>
            </div>
          </div>

          <Field label="New plan">
            <select
              className="input-clean w-full"
              data-testid="change-plan-select"
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
            >
              <option value="free">Free (Admin Grant — $0)</option>
              <option value="starter">Starter ($29/mo)</option>
              <option value="pro">Pro ($99/mo)</option>
              <option value="enterprise">Enterprise ($199/mo)</option>
            </select>
          </Field>

          <Field label="Duration (months)">
            <input
              type="number"
              min={1}
              max={120}
              className="input-clean w-full font-mono"
              data-testid="change-plan-months"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
            />
          </Field>

          <Field label="Reason (internal note — optional)">
            <textarea
              rows={2}
              className="input-clean w-full text-sm"
              data-testid="change-plan-reason"
              placeholder="e.g. Early partner · Internal QA · Loyalty comp"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>

          <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
            ⓘ This grant bypasses Stripe — no charge is made. It's logged in the admin audit trail.
          </div>

          <DialogFooter>
            <button type="button" className="btn-outline-dark" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary-dark flex items-center gap-2" data-testid="change-plan-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Grant plan
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Th({ children, className = "" }) { return <th className={`px-5 py-3 overline text-slate-600 ${className}`}>{children}</th>; }
function Td({ children, className = "", ...rest }) { return <td className={`px-5 py-4 ${className}`} {...rest}>{children}</td>; }


function EditLandlordDialog({ landlord, onClose, onSaved }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", company: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (landlord) {
      setForm({
        full_name: landlord.full_name || "",
        email: landlord.email || "",
        phone: landlord.phone || "",
        company: landlord.company || "",
      });
    }
  }, [landlord]);

  if (!landlord) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) {
      toast.error("Full name is required.");
      return;
    }
    if (!form.email.trim() || !/.+@.+\..+/.test(form.email)) {
      toast.error("A valid email is required.");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/landlords/${landlord.id}`, {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
      });
      toast.success("Profile updated");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!landlord} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="edit-landlord-dialog">
        <DialogHeader>
          <DialogTitle>Edit landlord</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Full name</span>
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="edit-landlord-full_name"
              className="w-full px-3 py-2 mt-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              required
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Email</span>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="edit-landlord-email"
              className="w-full px-3 py-2 mt-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              required
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Phone</span>
              <input
                type="text"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="edit-landlord-phone"
                className="w-full px-3 py-2 mt-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Company</span>
              <input
                type="text"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                data-testid="edit-landlord-company"
                className="w-full px-3 py-2 mt-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </label>
          </div>
          <DialogFooter>
            <button type="button" onClick={onClose} className="btn-outline-dark text-sm">Cancel</button>
            <button type="submit" disabled={busy} data-testid="edit-landlord-save" className="btn-primary text-sm">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteLandlordDialog({ landlord, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  if (!landlord) return null;
  const expected = (landlord.email || "").toLowerCase().trim();
  const matches = confirmText.trim().toLowerCase() === expected && expected.length > 0;

  const reset = () => {
    setConfirmText("");
    setBusy(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/landlords/${landlord.id}`);
      const cascade = data?.cascade || {};
      const summary = Object.entries(cascade)
        .map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`)
        .join(", ");
      toast.success(`Deleted ${landlord.email}${summary ? ` · cascade: ${summary}` : ""}`);
      reset();
      onDeleted?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={!!landlord}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose?.();
        }
      }}
    >
      <DialogContent className="max-w-md" data-testid="delete-landlord-dialog">
        <DialogHeader>
          <DialogTitle className="text-rose-700 inline-flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Delete landlord
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-900">
            <p>
              You're about to <strong>permanently delete</strong>{" "}
              <span className="font-mono">{landlord.email}</span>.
            </p>
            <p className="mt-2 text-xs">
              This will cascade-remove every property, lease, expense, loan,
              workspace, message, mention, todo, appointment, and lender
              package the landlord (and any teammates) own. Tenants tied to
              this landlord will be tombstoned but their records preserved.
              <br />
              <strong>This cannot be undone.</strong>
            </p>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest font-bold text-slate-500">
              Type <span className="font-mono normal-case">{landlord.email}</span> to confirm
            </span>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="delete-landlord-confirm-input"
              autoComplete="off"
              className="w-full px-3 py-2 mt-1 border border-rose-300 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-rose-700"
            />
          </label>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-outline-dark text-sm">Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !matches}
            data-testid="delete-landlord-confirm"
            className="text-sm px-4 py-2 rounded bg-rose-700 text-white font-semibold hover:bg-rose-800 disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            {busy ? "Deleting…" : "Delete permanently"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) { return <label className="block"><div className="overline text-slate-600 mb-1">{label}</div>{children}</label>; }

/**
 * Returns a compact human-readable relative-time label.
 *   < 60s      → "just now"
 *   < 60 min   → "3m ago"
 *   < 24 h     → "5h ago"
 *   < 7 days   → "3d ago"
 *   < 30 days  → "2w ago"
 *   otherwise  → locale date ("Feb 14, 2026")
 * Keeps the activity column glanceable at 250+ rows.
 */
function formatRelative(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Math.max(0, Date.now() - t);
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Reset-password dialog.
 * Two phases:
 *   1. `confirm` — warn the admin, one-click CTA to issue a new temp password
 *   2. `revealed` — show the new plaintext password ONCE with copy-to-clipboard,
 *      plus a reminder to relay it over a trusted channel.
 * The new password flag is also set server-side so the user is prompted to
 * change it on next login (must_change_password=true).
 */
function ResetPasswordDialog({ user, onClose }) {
  const [phase, setPhase] = useState("confirm");
  const [busy, setBusy] = useState(false);
  const [temp, setTemp] = useState("");
  const [show, setShow] = useState(true);
  const [copied, setCopied] = useState(false);

  // Reset internal state whenever the dialog opens for a different user.
  useEffect(() => {
    if (user) {
      setPhase("confirm");
      setTemp("");
      setShow(true);
      setCopied(false);
    }
  }, [user]);

  if (!user) return null;

  const issue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/reset-password`);
      setTemp(data.temp_password);
      setPhase("revealed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(temp);
      setCopied(true);
      toast.success("Password copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="reset-pw-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-amber-600" />
            {phase === "confirm" ? "Reset password" : "Temporary password issued"}
          </DialogTitle>
        </DialogHeader>

        {phase === "confirm" && (
          <div className="space-y-4" data-testid="reset-pw-confirm">
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-sm">
              <div className="overline text-slate-500 mb-1">User</div>
              <div className="font-semibold text-slate-900">{user.full_name || "—"}</div>
              <div className="text-slate-600 font-mono text-xs">{user.email}</div>
              <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                Role: {user.role || "landlord"}
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm text-amber-900 leading-relaxed">
              <div className="font-semibold mb-1 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Heads up
              </div>
              We can't recover their existing password — passwords are bcrypt-hashed and
              mathematically one-way. This action issues a <strong>new random temporary password</strong>,
              shows it to you once, and forces the user to change it on next login.
            </div>
            <div className="text-xs text-slate-500 leading-relaxed">
              Relay the temp password over a trusted channel (phone, SMS, in-person). The user's
              existing sessions remain signed in until they log out — consider asking them to sign
              out and back in with the new password.
            </div>
          </div>
        )}

        {phase === "revealed" && (
          <div className="space-y-4" data-testid="reset-pw-revealed">
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-sm">
              <div className="overline text-slate-500 mb-1">User</div>
              <div className="font-semibold text-slate-900">{user.full_name || "—"}</div>
              <div className="text-slate-600 font-mono text-xs">{user.email}</div>
            </div>
            <div>
              <div className="overline text-slate-600 mb-1">New temporary password</div>
              <div className="flex items-stretch gap-2">
                <div className="flex-1 bg-slate-900 text-emerald-300 font-mono text-lg px-4 py-3 rounded-sm tracking-wider select-all break-all" data-testid="reset-pw-value">
                  {show ? temp : "•".repeat(temp.length)}
                </div>
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="px-3 rounded-sm border border-slate-300 text-slate-600 hover:bg-slate-100"
                  title={show ? "Hide" : "Show"}
                  data-testid="reset-pw-toggle-visibility"
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={copy}
                  className="px-3 rounded-sm border border-slate-300 text-slate-600 hover:bg-slate-100"
                  title="Copy"
                  data-testid="reset-pw-copy"
                >
                  {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs text-emerald-900 leading-relaxed">
              This password is valid now. The user will be prompted to change it on their next sign-in.
              This reveal is available for 72 hours from the{" "}
              <Link to="/dashboard/admin/audit-log" className="underline font-semibold">audit log</Link>{" "}
              if you need to re-show it.
            </div>
            <div className="text-xs text-slate-500 leading-relaxed">
              Don't email this password. Relay it over a trusted channel (phone, SMS, in-person).
            </div>
          </div>
        )}

        <DialogFooter>
          {phase === "confirm" ? (
            <>
              <button type="button" onClick={onClose} className="btn-outline-dark text-sm" data-testid="reset-pw-cancel">Cancel</button>
              <button
                type="button"
                onClick={issue}
                disabled={busy}
                data-testid="reset-pw-issue"
                className="text-sm px-4 py-2 rounded bg-amber-600 text-white font-semibold hover:bg-amber-700 disabled:bg-slate-300 flex items-center gap-2"
              >
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Issuing…</> : <><KeyRound className="w-4 h-4" /> Issue new password</>}
              </button>
            </>
          ) : (
            <button type="button" onClick={onClose} className="btn-primary-dark text-sm" data-testid="reset-pw-close">
              Done
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

